import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, CalendarCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitBooking } from "@/lib/api";

const AGES = ["Toddler (1-5)", "Child (6-11)", "Teenager (12-18)", "Multiple Ages"];
const GOALS = [
  "Toddler Discipline & Meltdowns",
  "Teen Communication & Boundaries",
  "General Family Harmony & Systems",
  "Co-Parent Alignment",
];

const EMPTY = { fullName: "", email: "", childAge: "", primaryGoal: "", message: "" };

export default function BookingModal({ open, onOpenChange, prefillPlan }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...EMPTY,
        ...f,
        message: prefillPlan ? `I'm interested in the "${prefillPlan}" program.` : f.message,
      }));
    }
  }, [open, prefillPlan]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitBooking(form);
      toast.success("Thank you! Your discovery call request has been sent. Check your inbox shortly.");
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = "font-mono-accent text-xs uppercase tracking-[0.18em] text-[#2F3B2E]/70";
  const inputCls =
    "mt-2 w-full rounded-xl border border-[#2F3B2E]/20 bg-[#FAF7F0] px-4 py-3 text-sm outline-none focus:border-[#2F3B2E] transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-lg bg-[#FAF7F0] border-[#2F3B2E]/15 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="mb-1 inline-flex items-center gap-2 text-[#A8916B]">
            <CalendarCheck size={18} />
            <span className="font-mono-accent text-[11px] uppercase tracking-[0.2em]">Free · 20 minutes</span>
          </div>
          <DialogTitle className="font-serif-display text-2xl sm:text-3xl font-medium text-[#3B352D] text-left">
            Schedule Your Discovery Call
          </DialogTitle>
          <DialogDescription className="text-left text-[#6B665C]">
            Tell us a little about your family and we'll reach out to confirm a time.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className={labelCls}>Your Full Name *</label>
            <input
              data-testid="booking-fullname"
              value={form.fullName}
              onChange={update("fullName")}
              className={inputCls}
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className={labelCls}>Email Address *</label>
            <input
              data-testid="booking-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              className={inputCls}
              placeholder="jane@email.com"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Child's Age Range</label>
              <Select value={form.childAge} onValueChange={setField("childAge")}>
                <SelectTrigger data-testid="booking-age" className={inputCls + " flex justify-between"}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {AGES.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Primary Focus</label>
              <Select value={form.primaryGoal} onValueChange={setField("primaryGoal")}>
                <SelectTrigger data-testid="booking-goal" className={inputCls + " flex justify-between"}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {GOALS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Tell us briefly about your situation</label>
            <textarea
              data-testid="booking-message"
              value={form.message}
              onChange={update("message")}
              rows={3}
              className={inputCls + " resize-none"}
              placeholder="What would you love help with?"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2F3B2E] py-3.5 text-sm font-medium text-white hover:bg-[#232B22] hover:text-white transition-colors duration-200 disabled:opacity-70"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Sending..." : "Confirm Discovery Call"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
